import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Bot, LayoutTemplate, ShoppingBag, type LucideIcon } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { Eyebrow } from "@/components/landing/_primitives";

type Step = {
  icon: LucideIcon;
  eyebrow: { pt: string; en: string };
  title: { pt: string; en: string };
  body: { pt: string; en: string };
};

const steps: Step[] = [
  {
    icon: LayoutTemplate,
    eyebrow: { pt: "Capítulo 1", en: "Chapter 1" },
    title: { pt: "Desenhe sua identidade", en: "Shape your identity" },
    body: {
      pt: "Tipografia editorial, cor própria e composição premium. Sua página parece capa de revista — não mais um link genérico de bio.",
      en: "Editorial typography, your own palette, premium composition. Your page reads like a magazine cover — not a generic bio link.",
    },
  },
  {
    icon: ShoppingBag,
    eyebrow: { pt: "Capítulo 2", en: "Chapter 2" },
    title: { pt: "Conecte sua história", en: "Connect your story" },
    body: {
      pt: "Reúna o que você cria — produtos digitais, agenda, comunidade, coleções — em uma única superfície que conta quem você é.",
      en: "Gather what you create — digital products, booking, community, collections — into a single surface that tells who you are.",
    },
  },
  {
    icon: Bot,
    eyebrow: { pt: "Capítulo 3", en: "Chapter 3" },
    title: { pt: "Inspire sua audiência", en: "Inspire your audience" },
    body: {
      pt: "Um agente de IA conversa no seu tom, encaminha intenção e mantém sua presença viva — mesmo quando você está em silêncio criativo.",
      en: "An AI agent speaks in your voice, routes intent, and keeps your presence alive — even when you're off creating in silence.",
    },
  },
];

export function SocialProofBar() {
  const { language } = useLanguage();
  const rm = useReducedMotion();

  return (
    <section id="story" className="relative overflow-hidden bg-[#fffaf5] py-14 sm:py-20 lg:py-24">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-32 -top-20 h-[480px] w-[480px] rounded-full bg-[#fff1e1]/60 blur-[160px]" />
      </div>
      <div className="container">
        <motion.div
          initial={rm ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: rm ? 0 : 0.5 }}
          className="mx-auto max-w-3xl text-center mb-10"
        >
          <Eyebrow withDot accent="15 90% 58%" className="justify-center text-[#9a7258]">
            {pickByLanguage(language, { pt: "Sua jornada criativa", en: "Your creator journey" })}
          </Eyebrow>
          <h2 className="mt-4 font-editorial font-semibold text-[clamp(2rem,6vw,4rem)] leading-[1.05] sm:leading-[1.02] tracking-[-0.035em] text-[#201814]">
            {pickByLanguage(language, {
              pt: (
                <>
                  Três capítulos para uma presença{" "}
                  <span className="text-gradient-creator">que parece sua.</span>
                </>
              ),
              en: (
                <>
                  Three chapters to a presence{" "}
                  <span className="text-gradient-creator">that feels like you.</span>
                </>
              ),
            })}
          </h2>
          <p className="mt-5 text-[15px] leading-7 text-[#66584d] sm:text-lg sm:leading-8">
            {pickByLanguage(language, {
              pt: "Não é mais uma página de links. É a vitrine editorial da sua marca pessoal — desenhada para inspirar, conectar e converter.",
              en: "Not just another links page. It's the editorial showcase of your personal brand — designed to inspire, connect, and convert.",
            })}
          </p>
        </motion.div>

        <div className="grid gap-4 sm:gap-5 md:grid-cols-3">
          {steps.map((step, index) => {
            const Icon = step.icon;

            return (
              <motion.div
                key={step.title.en}
                initial={rm ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: rm ? 0 : 0.45, delay: index * 0.08 }}
                className="group relative overflow-hidden rounded-[24px] border border-[#eadbcc] bg-white/80 p-5 shadow-[0_18px_45px_rgba(109,77,49,0.06)] transition-[box-shadow,border-color] duration-200 hover:shadow-[0_28px_60px_rgba(109,77,49,0.10)] sm:rounded-[28px] sm:p-6"
              >
                {/* Top hairline — premium creator gradient signature */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-px"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, #ffb97a, #ef7b43, #d96079, #7d5cff, transparent)",
                  }}
                />
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#ffe6d3] text-[#ef7b43] transition-[background-color,opacity] duration-200">
                    <Icon className="h-5 w-5" />
                  </div>
                  <ArrowRight className="h-4 w-4 text-[#c9ab93] transition-transform duration-200 group-hover:translate-x-1" />
                </div>
                <Eyebrow withDot accent="15 90% 58%" className="mt-5 text-[#9a7258]">
                  {pickByLanguage(language, step.eyebrow)}
                </Eyebrow>
                <h3 className="mt-2 font-editorial text-[1.55rem] leading-[1.1] tracking-[-0.03em] text-[#241b17] sm:text-[1.85rem] sm:leading-[1.05] sm:tracking-[-0.04em] lg:text-[2rem] lg:leading-none">
                  {pickByLanguage(language, step.title)}
                </h3>
                <p className="mt-4 text-sm leading-7 text-[#64574d]">
                  {pickByLanguage(language, step.body)}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
